// Hover and click on the map's points and clusters, and the cards they raise.
//
// Mirrors the flowmap: a React root rendered into a detached element and handed
// to a real Mapbox popup, so the card is positioned and kept with its point by
// the map rather than by us.

import { createRoot } from 'react-dom/client';
import mapboxgl from 'mapbox-gl';

import HybridActivityClusterCard from './HybridActivityClusterCard';
import HybridActivityPointCard from './HybridActivityPointCard';
import {
    circleLayerId,
    circleLayerIds,
    clusterLayerId,
    clusterLayerIds,
    CLUSTER_SPLIT_MAX_ZOOM,
    pointsSourceId,
} from './hybridActivityLayer';

import s from './HybridActivityPointCard.module.scss';

// Clears the point's own disc, so the card never covers what it describes.
const CARD_OFFSET = 16;

// How far past a cluster's expansion zoom a click flies, matching the holdings
// clusters in utils/map/events.js so the two behave as one.
const CLUSTER_EXPANSION_OVERSHOOT = 1.4;

// The configured layer a point belongs to, for the card to name itself by and
// to wear the glyph of. A point whose value no layer claims is never drawn, so
// the null is only ever for a caller that handed no configuration in.
const layerFor = (config, properties) =>
    config?.layers?.find(
        (layer) => layer.matchValue === properties?.[config.column]
    ) ?? null;

const openPopup = (map, coordinates, content, { isPinned }) => {
    const element = document.createElement('div');
    const root = createRoot(element);

    root.render(content);

    const cardPopup = new mapboxgl.Popup({
        className: isPinned
            ? `${s.CardWrapper} ${s['CardWrapper--Pinned']}`
            : s.CardWrapper,
        offset: CARD_OFFSET,
        // Dismissal is ours to decide: a preview goes when the pointer leaves, a
        // pinned card when the reader says so.
        closeButton: false,
        closeOnClick: false,
        anchor: 'bottom',
        // A pinned card is somewhere to go — its close button and its source
        // links are the point of it. A preview is not, and would otherwise pull
        // focus out of the page on every point a reader passes over.
        focusAfterOpen: isPinned,
    })
        .setLngLat(coordinates)
        .setDOMContent(element)
        .addTo(map);

    return {
        remove: () => {
            cardPopup.remove();
            // Deferred: a card dismissed from its own close button is unmounting
            // the root it is rendering inside.
            setTimeout(() => root.unmount(), 0);
        },
    };
};

const openCard = (map, coordinates, properties, { layer, isPinned }) =>
    openPopup(
        map,
        coordinates,
        <HybridActivityPointCard
            properties={properties}
            layer={layer}
            isPinned={isPinned}
        />,
        { isPinned }
    );

const openClusterCard = (map, coordinates, items, { layer }) =>
    openPopup(
        map,
        coordinates,
        <HybridActivityClusterCard items={items} layer={layer} />,
        { isPinned: true }
    );

/**
 * Wire the map's points and clusters up to their cards, and return a teardown.
 *
 * Bound per configured layer, since every layer draws through its own Mapbox
 * layers — and to each layer's circle and cluster layers alone. The glyphs and
 * counts sit on symbol layers over the same sources and inside the same discs,
 * so listening to those too would make one point behave as two.
 *
 * Safe to call before the layers exist — Mapbox holds a layer-scoped listener
 * until there is a layer to fire it.
 */
export const attachPointCards = (map, config) => {
    let preview = null;
    let pinned = null;
    // The cluster handlers hear back from the source's worker after a round
    // trip, so their callbacks can land after teardown — a config change in
    // preview re-attaches everything — and would fly the map or open a card
    // nothing tracks any more.
    let isTornDown = false;
    // Touch readers only ever pin. A tap makes the browser synthesize the mouse
    // events a hover would, but never the mouseleave that ends one, so a preview
    // raised by a tap has no way to go away. Latched rather than sampled per
    // event, because a synthesized mouse event is indistinguishable from a real
    // one by the time it reaches us.
    let isTouch = false;

    const dismissPreview = () => {
        preview?.remove();
        preview = null;
    };

    const dismissPinned = () => {
        pinned?.remove();
        pinned = null;
    };

    const handleTouchStart = () => {
        isTouch = true;
        dismissPreview();
    };

    const handleMouseEnter = (e) => {
        map.getCanvas().style.cursor = 'pointer';

        // A pinned card holds this point's links; replacing it with a preview
        // that cannot would take them away mid-read.
        if (pinned || isTouch) {
            return;
        }

        dismissPreview();

        preview = openCard(map, e.lngLat, e.features[0].properties, {
            layer: layerFor(config, e.features[0].properties),
            isPinned: false,
        });
    };

    const handleMouseLeave = () => {
        map.getCanvas().style.cursor = '';
        dismissPreview();
    };

    // A tap on a touch device arrives here too, which is the whole of what
    // "usable on iPad" needs: a tap goes straight to pinned.
    const handleClick = (e) => {
        dismissPreview();
        dismissPinned();

        pinned = openCard(map, e.lngLat, e.features[0].properties, {
            layer: layerFor(config, e.features[0].properties),
            isPinned: true,
        });
    };

    // A cluster raises no preview, as a holdings cluster raises none: the
    // cursor alone says it can be clicked.
    const handleClusterEnter = () => {
        map.getCanvas().style.cursor = 'pointer';
    };

    const handleClusterLeave = () => {
        map.getCanvas().style.cursor = '';
    };

    // The holdings behaviour, per layer: a cluster the map can still split
    // flies in past its expansion zoom; one it cannot — points too close for
    // any zoom the tiles reach — opens a carousel of the points' own cards.
    const handleClusterClick = (layer) => (e) => {
        const cluster = e.features[0];
        const clusterId = cluster.properties.cluster_id;
        const source = map.getSource(pointsSourceId(layer.matchValue));

        if (!source) {
            return;
        }

        const center = cluster.geometry.coordinates;

        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err || isTornDown) {
                return;
            }

            // The expansion zoom answers from the cluster index, which runs
            // past the source's tiles: a zoom beyond CLUSTER_SPLIT_MAX_ZOOM
            // is one the map renders but the clustering never refines to, so
            // flying there would leave the cluster whole on screen.
            if (zoom <= Math.min(map.getMaxZoom(), CLUSTER_SPLIT_MAX_ZOOM)) {
                dismissPreview();
                dismissPinned();
                map.flyTo({
                    center,
                    zoom: zoom + CLUSTER_EXPANSION_OVERSHOOT,
                });
                return;
            }

            source.getClusterLeaves(
                clusterId,
                Infinity,
                0,
                (leavesErr, leaves) => {
                    if (leavesErr || isTornDown || !leaves?.length) {
                        return;
                    }

                    dismissPreview();
                    dismissPinned();
                    map.flyTo({ center, duration: 1000 });

                    pinned = openClusterCard(
                        map,
                        center,
                        leaves.map((leaf) => leaf.properties),
                        { layer }
                    );
                }
            );
        });
    };

    // Fires for every click, including the ones the handlers above already
    // took, so it has to tell a click on empty map from one that landed on a
    // point or a cluster.
    const handleMapClick = (e) => {
        if (!pinned) {
            return;
        }

        // No layers means no point can have been hit — and no card should be
        // open over points that are not drawn.
        const drawnLayers = [
            ...circleLayerIds(config),
            ...clusterLayerIds(config),
        ].filter((id) => map.getLayer(id));

        if (!drawnLayers.length) {
            dismissPinned();
            return;
        }

        // Only the incident layers count as a hit, so a click that lands on
        // a holdings marker dismisses the card like empty map would.
        // Deliberate: the card describes an incident, and a reader turning
        // to a holding has turned away from it.
        const hitFeatures = map.queryRenderedFeatures(e.point, {
            layers: drawnLayers,
        });

        if (!hitFeatures.length) {
            dismissPinned();
        }
    };

    const layerBindings = [];
    const bind = (event, layerId, handler) => {
        map.on(event, layerId, handler);
        layerBindings.push([event, layerId, handler]);
    };

    (config?.layers || []).forEach((layer) => {
        bind('mouseenter', circleLayerId(layer.matchValue), handleMouseEnter);
        bind('mouseleave', circleLayerId(layer.matchValue), handleMouseLeave);
        bind('click', circleLayerId(layer.matchValue), handleClick);

        bind(
            'mouseenter',
            clusterLayerId(layer.matchValue),
            handleClusterEnter
        );
        bind(
            'mouseleave',
            clusterLayerId(layer.matchValue),
            handleClusterLeave
        );
        bind(
            'click',
            clusterLayerId(layer.matchValue),
            handleClusterClick(layer)
        );
    });

    map.on('touchstart', handleTouchStart);
    map.on('click', handleMapClick);

    return () => {
        isTornDown = true;
        layerBindings.forEach(([event, layerId, handler]) => {
            map.off(event, layerId, handler);
        });
        map.off('touchstart', handleTouchStart);
        map.off('click', handleMapClick);

        dismissPreview();
        dismissPinned();
    };
};
